import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { TemplateDesignElement } from '../../design-element';
import {
  CommunicationTemplate,
  CommunicationTemplateViewControl,
  MassNotificationTemplate,
  MessageSettings,
  TemplateViewControlType
} from 'src/app/modules/communications/store/communications';
import { TemplateDesignerService } from 'src/app/modules/communications/services/template-designer.service';
import { Dataset, DatasetType, DocumentQueryParams, DocumentReader, RuleOperator, SchemaField } from '@app/modules/core/store/models';
import { FieldToValueMapping } from '../../design-elements.models';
import { TimelineApiService } from 'src/app/modules/incidents/store/timelines/timelines-api.service';
import { Timeline, TimelineInterviewType } from '@app/modules/incidents/store/timelines/timelines.model';
import { CultureService } from '@app/modules/core/services/culture.service';
import { DatasetService } from '@app/modules/core/services/dataset.service';
import { DatasetApiService } from '@app/modules/core/services/dataset-api.service';
import { InvolvedPartyApiService } from '@app/modules/incidents/store/involved-party/involved-party-api.service';

@Component({
    selector: 'topo-template-timeline-element',
    templateUrl: './timeline-control.component.html',
    styleUrls: ['./timeline-control.component.scss'],
    standalone: false
})
export class TemplateDesignTimelineElement implements OnInit, OnDestroy, TemplateDesignElement {
  @Input() index: number;
  @Input() messageSettings?: MessageSettings;
  @Input() template?: CommunicationTemplate;
  @Input() control?: CommunicationTemplateViewControl;
  @Input() set culture(value: string) {
    this._culture = value;
    if (!this.editMode && !!value) {
      this.render();
    }
  }
  _culture: string;
  @Input() orgLanguages: string[];
  @Input() selected: boolean;
  @Output() settingsChanged = new EventEmitter<{ uId: string; settings: any }>();
  @Output() hideControl = new EventEmitter();

  editMode: boolean;
  timeLineHeaderText: string;
  timelines: {
    category: string;
    createdName: string;
    createdDate: string;
    interviewLabels: { date?: string; interviwee?: string;  interviewer?: string},
    interview: { date?: string; interviwee?: { name: string; type: string }; interviewer?: { name: string; type: string } };
    description: string;
  }[] = [
    {
      category: 'Update',
      createdName: 'John Smith',
      createdDate: '1/1/2024',
      interviewLabels: null,
      interview: null,
      description: 'This is an example timeline entry.'
    }
  ];

  constructor(
    private translateService: TranslateService,
    private templateDesignerService: TemplateDesignerService,
    private timelineApiService: TimelineApiService,
    private partyApiService: InvolvedPartyApiService,
    private dataseApiService: DatasetApiService,
    private cultureService: CultureService
  ) {}

  ngOnInit(): void {}

  ngOnDestroy(): void {}

  loadSettings(): void {
    this.editMode = true;
  }
  saveSettings(): boolean {
    return true;
  }
  render(): void {
    this.editMode = false;
    this.timeLineHeaderText = this.templateDesignerService.getStaticTranslateText('TIMELINE-HEADER', this._culture);
  }
  edit(): void {}
  setRuntimeData(data: any): void {}

  setDocumentContext(dataset: Dataset, document: Document, mapUId: string, mapping: FieldToValueMapping) {
    if (dataset?.type !== DatasetType.Incident) {
      this.timelines = null;
      this.hideControl?.emit();
      return;
    }
    this.timelines = [];
    if (!this.editMode) {
      const timelineField: SchemaField = dataset.schema.fields.find(f => f.systemName === 'timelines');
      const partyField: SchemaField = dataset.schema.fields.find(f => f.systemName === 'involved_parties');

      if (!!timelineField?.settings) {
        const timelinesDatasetUId = timelineField.settings.relatedDatasetUId;
        const partyDatasetUId = partyField.settings.relatedDatasetUId;

        this.dataseApiService.getDataset(timelinesDatasetUId, { includeWorkflow: false }).subscribe(ds => {
          const categoryField: SchemaField = ds.schema.fields.find(f => f.systemName === 'category');
          const query = {
            filterGroup: {},
            sort: 'date_created asc',
            pagination: {
              skip: 0,
              take: 1000
            }
          } as DocumentQueryParams;
          this.timelineApiService.getTimelines(dataset.uId, document['uid'], timelinesDatasetUId, query).subscribe(res => {
            res?.data?.forEach(d => {
              const reader = new DocumentReader(d);
              const timeline = {
                category: categoryField?.picklistValues.find(p => p.uId === reader.getFieldValue('category'))?.value,
                createdName: reader.getFieldValue('created_by_name'),
                createdDate: reader.formatDate(reader.dateCreated.toString(), null, this.cultureService),
                interview: null,
                interviewLabels: null,
                description: reader.getFieldValue('description')
              };

              this.timelines.push(timeline);
              if (reader.getFieldValue('category') === TimelineInterviewType) {
                timeline.interviewLabels = {};
                timeline.interviewLabels.date = ds.schema.fields.find(f => f.systemName === "interview_date")?.displayName;
                timeline.interviewLabels.interviwee = ds.schema.fields.find(f => f.systemName === "interviewee")?.displayName;
                timeline.interviewLabels.interviewer = ds.schema.fields.find(f => f.systemName === "interviewer")?.displayName;
                this.loadInterviewDetails(dataset.uId, document['uid'], timeline, partyDatasetUId, reader);
              }
              this.render();
            });
          });
        });
      }
    }
  }

  private loadInterviewDetails(incidentDatasetUId: string,
    incidentUId: string,
    timeline: {
    category: string;
    createdName: string;
    createdDate: string;
    interview: { date: string; interviwee: { name: string; type: string }; interviewer: { name: string; type: string } };
    description: string;
    }, partyDatasetUId: string, timelineReader: DocumentReader) {

    timeline.interview = {date: null, interviwee: null, interviewer: null};
    const interviewDate = timelineReader.getFieldValue('interview_date');
    const interviewee = timelineReader.getFieldValue("interviewee");
    const interviewer = timelineReader.getFieldValue("interviewer");
    const query = {
      filterGroup: {
        filters: []
      },
      pagination: {
        skip: 0,
        take: 2
      }
    } as DocumentQueryParams;

    if (!!interviewDate) {
      timeline.interview.date = timelineReader.formatDate(interviewDate, "DATE-ONLY", this.cultureService);
    }
    const ids = [];
    if (!!interviewee) {
      ids.push(interviewee);
    }
    if (!!interviewer) {
      ids.push(interviewer);
    }
    if (ids.length) {
      query.filterGroup.filters.push({
        field: "uid",
        operator: RuleOperator.In,
        value: ids.join(",")
      });
    }
    this.partyApiService.getInvolvedParties(incidentDatasetUId, incidentUId, partyDatasetUId, query).subscribe(res => {
      res?.data?.forEach(d => {
        const reader = new DocumentReader(d);
        if (reader.uId === interviewee) {
          const displayName = reader.getFieldValue("display_name");
          if (!!displayName) {
            const parts = displayName.split('~');
            timeline.interview.interviwee = {
              name: parts[0],
              type: parts[1]
            }
          }
        }
        if (reader.uId === interviewer) {
          const displayName = reader.getFieldValue("display_name");
          if (!!displayName) {
            const parts = displayName.split('~');
            timeline.interview.interviewer = {
              name: parts[0],
              type: parts[1]
            }
          }
        }
      });
    });
  }
}
